import React, { useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthContext, AuthProvider } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import TruckList from './pages/truck/TruckList';
import TruckForm from './pages/truck/TruckForm';
import TruckDetail from './pages/truck/TruckDetail';
import ChargeDetail from './pages/truck/ChargeDetail';
import InvoiceCostList from './pages/accounting/InvoiceCostList';
import ProfitReport from './pages/accounting/ProfitReport';
import OceanImportList from './pages/ocean-import/OceanImportList';
import OceanImportDetail from './pages/ocean-import/OceanImportDetail';
import OceanExportList from './pages/ocean-export/OceanExportList';
import OceanExportDetail from './pages/ocean-export/OceanExportDetail';
import AirImportList from './pages/air-import/AirImportList';
import AirImportDetail from './pages/air-import/AirImportDetail';
import AirExportList from './pages/air-export/AirExportList';
import AirExportDetail from './pages/air-export/AirExportDetail';
import MiscList from './pages/misc/MiscList';
import MiscDetail from './pages/misc/MiscDetail';
import TradePartnerList from './pages/trade-partner/TradePartnerList';
import TradePartnerDetail from './pages/trade-partner/TradePartnerDetail';
import Reports from './pages/Reports';
import DeliveryOrder from './pages/documents/DeliveryOrder';
import ArrivalNotice from './pages/documents/ArrivalNotice';
import BillOfLading from './pages/documents/BillOfLading';
import AuthorityToMakeEntry from './pages/documents/AuthorityToMakeEntry';
import InvoicePrint from './pages/documents/InvoicePrint';
import CreditNotePrint from './pages/documents/CreditNotePrint';
import PaymentForm from './pages/accounting/PaymentForm';
import PaymentList from './pages/accounting/PaymentList';
import BankReconciliation from './pages/accounting/BankReconciliation';
import BalanceSheet from './pages/accounting/BalanceSheet';
import IncomeStatement from './pages/accounting/IncomeStatement';
import AgingReport from './pages/accounting/AgingReport';
import CustomerStatement from './pages/accounting/CustomerStatement';
import SearchResults from './pages/SearchResults';
import ActivityLog from './pages/ActivityLog';
import UserManagement from './pages/UserManagement';
import ISFList from './pages/isf/ISFList';
import ISFDetail from './pages/isf/ISFDetail';
import ChatWidget from './components/ChatWidget';

const ProtectedRoute = ({ children }) => {
  const { token, loading } = useContext(AuthContext);

  if (loading) {
    return <div className="loading"><div className="loading-spinner"></div></div>;
  }

  if (!token) {
    return <Navigate to="/login" />;
  }

  return children;
};

const AuthRoute = ({ children }) => {
  const { token, loading } = useContext(AuthContext);

  if (loading) {
    return <div className="loading"><div className="loading-spinner"></div></div>;
  }

  if (token) {
    return <Navigate to="/" />;
  }

  return children;
};

function AppRoutes() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
        {/* Registration is admin-only via API — no public register page */}

        {/* Document preview pages (open in new tab, no Layout wrapper) */}
        <Route path="/documents/truck/:id/delivery-order" element={<ProtectedRoute><DeliveryOrder /></ProtectedRoute>} />
        <Route path="/documents/truck/:id/arrival-notice" element={<ProtectedRoute><ArrivalNotice /></ProtectedRoute>} />
        <Route path="/documents/truck/:id/bill-of-lading" element={<ProtectedRoute><BillOfLading /></ProtectedRoute>} />
        <Route path="/documents/air-import/:id/delivery-order" element={<ProtectedRoute><DeliveryOrder /></ProtectedRoute>} />
        <Route path="/documents/air-import/:id/arrival-notice" element={<ProtectedRoute><ArrivalNotice /></ProtectedRoute>} />
        <Route path="/documents/air-import/:id/bill-of-lading" element={<ProtectedRoute><BillOfLading /></ProtectedRoute>} />
        <Route path="/documents/air-import/:id/authority-to-make-entry" element={<ProtectedRoute><AuthorityToMakeEntry /></ProtectedRoute>} />
        <Route path="/documents/air-export/:id/delivery-order" element={<ProtectedRoute><DeliveryOrder /></ProtectedRoute>} />
        <Route path="/documents/air-export/:id/arrival-notice" element={<ProtectedRoute><ArrivalNotice /></ProtectedRoute>} />
        <Route path="/documents/air-export/:id/bill-of-lading" element={<ProtectedRoute><BillOfLading /></ProtectedRoute>} />
        <Route path="/documents/ocean-import/:id/delivery-order" element={<ProtectedRoute><DeliveryOrder /></ProtectedRoute>} />
        <Route path="/documents/ocean-import/:id/arrival-notice" element={<ProtectedRoute><ArrivalNotice /></ProtectedRoute>} />
        <Route path="/documents/ocean-import/:id/bill-of-lading" element={<ProtectedRoute><BillOfLading /></ProtectedRoute>} />
        <Route path="/documents/ocean-export/:id/delivery-order" element={<ProtectedRoute><DeliveryOrder /></ProtectedRoute>} />
        <Route path="/documents/ocean-export/:id/arrival-notice" element={<ProtectedRoute><ArrivalNotice /></ProtectedRoute>} />
        <Route path="/documents/ocean-export/:id/bill-of-lading" element={<ProtectedRoute><BillOfLading /></ProtectedRoute>} />
        <Route path="/documents/misc/:id/delivery-order" element={<ProtectedRoute><DeliveryOrder /></ProtectedRoute>} />
        <Route path="/documents/misc/:id/arrival-notice" element={<ProtectedRoute><ArrivalNotice /></ProtectedRoute>} />
        <Route path="/documents/misc/:id/bill-of-lading" element={<ProtectedRoute><BillOfLading /></ProtectedRoute>} />

        {/* Charge print documents (Invoice / Credit Note) */}
        <Route path="/documents/charge/:chargeId/invoice" element={<ProtectedRoute><InvoicePrint /></ProtectedRoute>} />
        <Route path="/documents/charge/:chargeId/credit-note" element={<ProtectedRoute><CreditNotePrint /></ProtectedRoute>} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="dashboard" element={<Dashboard />} />

          <Route path="truck" element={<TruckList />} />
          <Route path="truck/new" element={<TruckForm />} />
          <Route path="truck/:id" element={<TruckDetail />} />
          <Route path="truck/:id/edit" element={<TruckForm />} />

          <Route path="ocean-import" element={<OceanImportList />} />
          <Route path="ocean-import/new" element={<OceanImportDetail />} />
          <Route path="ocean-import/:id" element={<OceanImportDetail />} />
          <Route path="ocean-export" element={<OceanExportList />} />
          <Route path="ocean-export/new" element={<OceanExportDetail />} />
          <Route path="ocean-export/:id" element={<OceanExportDetail />} />
          <Route path="air-import" element={<AirImportList />} />
          <Route path="air-import/new" element={<AirImportDetail />} />
          <Route path="air-import/:id" element={<AirImportDetail />} />
          <Route path="air-export" element={<AirExportList />} />
          <Route path="air-export/new" element={<AirExportDetail />} />
          <Route path="air-export/:id" element={<AirExportDetail />} />
          <Route path="misc" element={<MiscList />} />
          <Route path="misc/new" element={<MiscDetail />} />
          <Route path="misc/:id" element={<MiscDetail />} />

          <Route path="isf" element={<ISFList />} />
          <Route path="isf/new" element={<ISFDetail />} />
          <Route path="isf/:id" element={<ISFDetail />} />

          <Route path="trade-partner" element={<TradePartnerList />} />
          <Route path="trade-partner/new" element={<TradePartnerDetail />} />
          <Route path="trade-partner/:id" element={<TradePartnerDetail />} />

          <Route path="charge/new" element={<ChargeDetail />} />
          <Route path="charge/:chargeId" element={<ChargeDetail />} />

          <Route path="invoice-cost" element={<InvoiceCostList />} />
          <Route path="invoice-list" element={<InvoiceCostList listType="AR" />} />
          <Route path="cost-list" element={<InvoiceCostList listType="AP" />} />
          <Route path="profit-report" element={<ProfitReport />} />

          <Route path="payment/new" element={<PaymentForm />} />
          <Route path="payment/:paymentId" element={<PaymentForm />} />
          <Route path="payment-received-list" element={<PaymentList paymentType="receive" />} />
          <Route path="payment-made-list" element={<PaymentList paymentType="make" />} />

          <Route path="bank-reconciliation" element={<BankReconciliation />} />
          <Route path="balance-sheet" element={<BalanceSheet />} />
          <Route path="income-statement" element={<IncomeStatement />} />
          <Route path="aging-report" element={<AgingReport />} />
          <Route path="customer-statement" element={<CustomerStatement />} />

          <Route path="search" element={<SearchResults />} />
          <Route path="reports" element={<Reports />} />
          <Route path="activity-log" element={<ActivityLog />} />
          <Route path="users" element={<UserManagement />} />
        </Route>
      </Routes>

      <ChatWidget />

      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
